/**
 * Calendar header with independent month and year selectors.
 */

import type * as React from "react";
import { cn } from "../../../../lib/utils";
import { IconButton } from "../../../buttons/icon-button";
import { Icon } from "../../../icon";
import { formatMonthYear } from "../../shared/calendar-model";
import type { DisplayMode, ResolvedDateLocale } from "../../shared/types";

export type CalendarSelector = "month" | "year" | null;

interface CalendarHeaderProps {
	displayedMonthMs: number;
	activeSelector: CalendarSelector;
	displayMode: DisplayMode;
	showModeToggle: boolean;
	title: React.ReactNode;
	headline: React.ReactNode;
	locale: ResolvedDateLocale;
	canNavigatePrev: boolean;
	canNavigateNext: boolean;
	canNavigatePreviousYear: boolean;
	canNavigateNextYear: boolean;
	onPrev(): void;
	onNext(): void;
	onPreviousYear(): void;
	onNextYear(): void;
	onToggleSelector(selector: Exclude<CalendarSelector, null>): void;
	onToggleMode(): void;
}

const selectorClassName =
	"flex items-center gap-1 rounded-m3-full px-2 py-1 text-[length:var(--md-typescale-title-small-size,14px)] font-[number:var(--md-typescale-title-small-weight,500)] text-m3-on-surface transition-m3-fast-effects hover:bg-m3-on-surface/8 focus-visible:outline-2 focus-visible:outline-m3-primary";

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
	displayedMonthMs,
	activeSelector,
	displayMode,
	showModeToggle,
	title,
	headline,
	locale,
	canNavigatePrev,
	canNavigateNext,
	canNavigatePreviousYear,
	canNavigateNextYear,
	onPrev,
	onNext,
	onPreviousYear,
	onNextYear,
	onToggleSelector,
	onToggleMode,
}) => {
	const date = new Date(displayedMonthMs);
	const monthLabel = new Intl.DateTimeFormat(locale.locale, {
		month: "short",
		timeZone: "UTC",
	}).format(date);
	const yearLabel = String(date.getUTCFullYear());

	return (
		<div className="flex flex-col gap-1 px-6 pb-4 pt-5">
			<div className="text-(length:--md-typescale-label-large-size,14px) font-(--md-typescale-label-large-weight,500) text-m3-on-surface-variant">
				{title}
			</div>
			<div className="mb-2 leading-tight text-(length:--md-typescale-headline-large-size,32px) font-(--md-typescale-headline-large-weight,400) text-m3-on-surface-variant">
				{headline}
			</div>

			{displayMode === "picker" && (
				<div className="flex min-h-10 items-center gap-0.5">
					{activeSelector === null && (
						<IconButton
							onClick={onPrev}
							aria-label="Previous month"
							disabled={!canNavigatePrev}
							size="xs"
						>
							<Icon name="chevron_left" />
						</IconButton>
					)}
					<button
						type="button"
						onClick={() => onToggleSelector("month")}
						aria-expanded={activeSelector === "month"}
						aria-controls="date-picker-selection"
						className={cn(
							selectorClassName,
							activeSelector === "month" && "bg-m3-on-surface/8",
						)}
					>
						{monthLabel}
						<Icon
							name="arrow_drop_down"
							className={cn(
								"text-[20px] transition-transform duration-200",
								activeSelector === "month" && "rotate-180",
							)}
						/>
					</button>
					{activeSelector === null && (
						<IconButton
							onClick={onNext}
							aria-label="Next month"
							disabled={!canNavigateNext}
							size="xs"
						>
							<Icon name="chevron_right" />
						</IconButton>
					)}

					<div className="ml-auto flex items-center gap-0.5">
						{activeSelector === null && (
							<IconButton
								onClick={onPreviousYear}
								aria-label="Previous year"
								disabled={!canNavigatePreviousYear}
								size="xs"
							>
								<Icon name="chevron_left" />
							</IconButton>
						)}
						<button
							type="button"
							onClick={() => onToggleSelector("year")}
							aria-expanded={activeSelector === "year"}
							aria-controls="date-picker-selection"
							className={cn(
								selectorClassName,
								activeSelector === "year" && "bg-m3-on-surface/8",
							)}
						>
							{yearLabel}
							<Icon
								name="arrow_drop_down"
								className={cn(
									"text-[20px] transition-transform duration-200",
									activeSelector === "year" && "rotate-180",
								)}
							/>
						</button>
						{activeSelector === null && (
							<IconButton
								onClick={onNextYear}
								aria-label="Next year"
								disabled={!canNavigateNextYear}
								size="xs"
							>
								<Icon name="chevron_right" />
							</IconButton>
						)}
						{showModeToggle && (
							<IconButton
								onClick={onToggleMode}
								aria-label="Switch to text input"
								size="xs"
							>
								<Icon name="keyboard" />
							</IconButton>
						)}
					</div>
				</div>
			)}
			{displayMode === "input" && showModeToggle && (
				<IconButton
					onClick={onToggleMode}
					aria-label="Switch to calendar"
					size="xs"
					className="self-end"
				>
					<Icon name="calendar_month" />
				</IconButton>
			)}
			<span className="sr-only">
				{formatMonthYear(displayedMonthMs, locale)}
			</span>
		</div>
	);
};

CalendarHeader.displayName = "CalendarHeader";
